import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Dashboard,
  People,
  Settings,
  Assessment,
  Inventory2,
  Api,
  Badge,
  FactCheck,
  VerifiedUser,
  Devices,
  History,
  GppMaybe,
  Policy,
  Business,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  canAccessIntegrations,
  canAccessSettings,
  canManageOrganizations,
  canManageUsers,
  isAuditor as roleIsAuditor,
} from "../utils/roles";

const drawerWidth = 260;
const miniDrawerWidth = 76;

const buildGroups = (role) => {
  if (role === "Employee") {
    return [
      {
        heading: "My Assets",
        items: [
          { text: "My Devices", icon: <Devices />, route: "/my-devices" },
          { text: "Undertaking history", icon: <Badge />, route: "/asset" },
        ],
      },
    ];
  }

  const isAuditor = roleIsAuditor(role);
  const management = [
    canManageUsers(role) && { text: "Users", icon: <People />, route: "/users" },
    { text: "Inventory", icon: <Inventory2 />, route: "/assets" },
    { text: "Undertakings", icon: <Badge />, route: "/asset" },
    { text: "Verification", icon: <VerifiedUser />, route: "/verification" },
    !isAuditor && { text: "Corrections", icon: <FactCheck />, route: "/reviews" },
    { text: "Warranty", icon: <GppMaybe />, route: "/warranty" },
    { text: "Compliance", icon: <Policy />, route: "/compliance" },
  ].filter(Boolean);

  const system = [
    canManageOrganizations(role) && {
      text: "Organizations",
      icon: <Business />,
      route: "/organizations",
    },
    { text: "Audit Log", icon: <History />, route: "/audit" },
    canAccessIntegrations(role) && {
      text: "Integrations",
      icon: <Api />,
      route: "/integrations",
    },
    canAccessSettings(role) && { text: "Settings", icon: <Settings />, route: "/settings" },
  ].filter(Boolean);

  return [
    {
      heading: "Overview",
      items: [
        { text: "Dashboard", icon: <Dashboard />, route: "/dashboard" },
        { text: "Analytics", icon: <Assessment />, route: "/analytics" },
      ],
    },
    { heading: "Management", items: management },
    { heading: "System", items: system },
  ];
};

const AdminSidebar = ({ open = true, isMobile = false, onClose = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = JSON.parse(localStorage.getItem("adminUser") || "null");
  const groups = buildGroups(admin?.role);

  const handleMenuClick = (route) => {
    navigate(route);
    if (isMobile) onClose();
  };

  const expanded = isMobile ? true : open;
  const initials = (admin?.name || admin?.email || "A").trim().charAt(0).toUpperCase();

  const renderItem = (item) => {
    const selected = location.pathname === item.route;
    const button = (
      <ListItemButton
        onClick={() => handleMenuClick(item.route)}
        selected={selected}
        sx={{
          minHeight: 46,
          justifyContent: expanded ? "initial" : "center",
          px: expanded ? 2 : 1.5,
          position: "relative",
          color: "rgba(226,232,240,0.75)",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "#fff" },
          "&.Mui-selected": {
            backgroundColor: "rgba(129,140,248,0.16)",
            color: "#fff",
            "& .MuiListItemIcon-root": { color: "#a5b4fc" },
            "&:hover": { backgroundColor: "rgba(129,140,248,0.24)" },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: 3,
              background: "linear-gradient(180deg,#818cf8,#c084fc)",
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? 2 : "auto",
            justifyContent: "center",
            color: selected ? "#a5b4fc" : "rgba(148,163,184,0.9)",
          }}
        >
          {item.icon}
        </ListItemIcon>
        {expanded && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: selected ? 700 : 500 }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
        {expanded ? button : <Tooltip title={item.text} placement="right">{button}</Tooltip>}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg,#0f172a 0%,#1e1b4b 100%)",
        color: "#e2e8f0",
      }}
    >
      <Toolbar sx={{ px: expanded ? 2.5 : 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            justifyContent: expanded ? "flex-start" : "center",
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.5,
              background: "linear-gradient(135deg,#6366f1,#c026d3)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 18,
              flexShrink: 0,
              boxShadow: "0 6px 16px rgba(99,102,241,0.45)",
            }}
          >
            A
          </Box>
          {expanded && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>
                Asset Admin
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(148,163,184,0.9)" }}>
                Undertaking System
              </Typography>
            </Box>
          )}
        </Box>
      </Toolbar>

      <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden", mt: 1 }}>
        {groups.map((group) => (
          <Box key={group.heading} sx={{ mb: 1 }}>
            {expanded ? (
              <Typography
                variant="overline"
                sx={{
                  px: 3,
                  color: "rgba(148,163,184,0.6)",
                  fontSize: "0.68rem",
                  display: "block",
                  mb: 0.5,
                }}
              >
                {group.heading}
              </Typography>
            ) : (
              <Box sx={{ height: 1, mx: 2, my: 1.5, bgcolor: "rgba(148,163,184,0.15)" }} />
            )}
            <List disablePadding>{group.items.map(renderItem)}</List>
          </Box>
        ))}
      </Box>

      {/* User footer */}
      <Box
        sx={{
          p: expanded ? 2 : 1.5,
          borderTop: "1px solid rgba(148,163,184,0.15)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          justifyContent: expanded ? "flex-start" : "center",
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: 15,
            fontWeight: 700,
            background: "linear-gradient(135deg,#6366f1,#c026d3)",
          }}
        >
          {initials}
        </Avatar>
        {expanded && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ color: "#fff", fontWeight: 600 }}>
              {admin?.name || admin?.email || "Admin"}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: "rgba(148,163,184,0.9)", display: "block" }}>
              {admin?.role || "Administrator"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : miniDrawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : miniDrawerWidth,
          transition: "width 0.25s ease",
          overflowX: "hidden",
          border: "none",
        },
      }}
      open={open}
    >
      {drawerContent}
    </Drawer>
  );
};

export { drawerWidth, miniDrawerWidth };
export default AdminSidebar;
